/**
 * SourcingConfirm — review a matched fill and confirm it. Shows the server-quoted
 * line items (never client-computed rates — invariant 4: we only SUM the amounts
 * the match already carries), an optional return-protection toggle, and the rail
 * picker. Runs the accept-total handshake: a stone that repriced between match and
 * confirm comes back 409 and we re-prompt the buyer with the NEW total before we
 * ever charge it (a number they've seen, never a surprise).
 */
import { useMemo, useState } from 'react'
import { sourcingApi } from '../../api/endpoints'
import { ApiError } from '../../api/types'
import { formatGuarantee, formatSourcingFee, type SourcingMatch } from '../../lib/sourcing'

type Rail = 'ach' | 'wire' | 'card'

interface Props {
  orderId: string
  match: SourcingMatch
  onDone: () => void
  onCancel: () => void
}

const money = (n: number) => `$${(Number.isFinite(n) ? n : 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`

export function SourcingConfirm({ orderId, match, onDone, onCancel }: Props) {
  const [rail, setRail] = useState<Rail>('ach')
  const [guarantee, setGuarantee] = useState(match.guaranteeSelected)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // When the stone repriced, we hold the server's new total until the buyer
  // re-confirms it. Null = quote is fresh.
  const [reprice, setReprice] = useState<{ total: number; from: number } | null>(null)
  const [done, setDone] = useState(false)

  // Sum the server-provided line items for THIS rail/guarantee choice.
  const displayTotal = useMemo(() => {
    if (reprice) return reprice.total
    let t = match.quotedBase + match.sourcingFeeUsd
    if (guarantee) t += match.guaranteeFeeUsd
    if (rail === 'card') t += match.cardSurchargeUsd
    return Math.round(t * 100) / 100
  }, [reprice, match, guarantee, rail])

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      const res = await sourcingApi.confirm(orderId, {
        payMethod: rail,
        guaranteeSelected: guarantee,
        acceptTotal: displayTotal,
      })
      if (res.ok) { setDone(true); return }
      setError('Could not complete the fill. Please try again.')
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = (err.body ?? {}) as { code?: string; error?: string; total?: number; priceNow?: number }
        if (body.code === 'stone_price_changed') {
          setReprice({ total: body.total ?? displayTotal, from: displayTotal })
          setError(null)
          return
        }
        if (body.code === 'stone_gone' || body.code === 'not_matched') {
          setError('This match is no longer available — we’ll keep searching and match you to the next one.')
          setTimeout(onDone, 1400)
          return
        }
        setError(body.error ?? 'Please re-confirm the current price.')
        return
      }
      setError('Could not complete the fill. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="rounded-card border border-emerald-200 bg-emerald-50 p-4 text-center">
        <p className="text-sm font-semibold text-emerald-800">Order placed</p>
        <p className="mt-1 text-xs text-emerald-700">Track it in your orders. We’ll coordinate the rest.</p>
        <button onClick={onDone} className="mt-3 text-xs font-semibold text-emerald-800 underline">Done</button>
      </div>
    )
  }

  return (
    <div className="rounded-card border border-gold/40 bg-gold/[0.04] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-ink">We found a match</p>
        <span className="text-[11px] font-mono text-soft">#{match.stockId}</span>
      </div>

      {/* Line items — all server-quoted */}
      <dl className="space-y-1.5 text-sm">
        <div className="flex justify-between"><dt className="text-soft">Stone</dt><dd className="text-ink font-medium">{money(match.quotedBase)}</dd></div>
        <div className="flex justify-between">
          <dt className="text-soft">Sourcing</dt>
          <dd className="text-ink font-medium">{formatSourcingFee(match.sourcingFeeUsd)}</dd>
        </div>
        {guarantee && (
          <div className="flex justify-between"><dt className="text-soft">Return protection</dt><dd className="text-ink font-medium">{money(match.guaranteeFeeUsd)}</dd></div>
        )}
        {rail === 'card' && match.cardSurchargeUsd > 0 && (
          <div className="flex justify-between"><dt className="text-soft">Card fee</dt><dd className="text-ink font-medium">{money(match.cardSurchargeUsd)}</dd></div>
        )}
        <div className="flex justify-between border-t border-border pt-1.5">
          <dt className="font-semibold text-ink">Total</dt><dd className="font-bold text-ink">{money(displayTotal)}</dd>
        </div>
      </dl>

      {/* Return protection toggle */}
      <label className="flex items-start gap-2.5 text-sm text-ink cursor-pointer select-none">
        <input type="checkbox" checked={guarantee} onChange={(e) => { setGuarantee(e.target.checked); setReprice(null) }}
          className="mt-0.5 h-4 w-4 rounded border-border text-gold focus:ring-gold/30" />
        <span>Add return protection <span className="text-soft">({formatGuarantee(match.member)}{match.guaranteeFeeUsd > 0 ? ` · ${money(match.guaranteeFeeUsd)}` : ''})</span> — if the stone doesn’t match grade, we replace or reprice it.</span>
      </label>

      {/* Rail */}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-soft uppercase tracking-wide">Pay by</p>
        <div className="flex gap-2">
          {([['ach', 'Bank (ACH)'], ['wire', 'Wire'], ['card', 'Card']] as [Rail, string][]).map(([r, label]) => (
            <button key={r} type="button" onClick={() => { setRail(r); setReprice(null) }}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${rail === r ? 'bg-navy text-white' : 'bg-navy/5 text-ink hover:bg-navy/10'}`}>
              {label}
            </button>
          ))}
        </div>
        {rail === 'card' && match.cardSurchargeUsd > 0 && <p className="text-xs text-soft">Card adds a processing fee — bank or wire has none.</p>}
      </div>

      {reprice && (
        <p className="text-sm font-medium text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
          The supplier repriced this stone to {money(reprice.total)}. Confirm to proceed at the new total.
        </p>
      )}
      {error && <p className="text-sm font-medium text-red-600">{error}</p>}

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} disabled={busy}
          className="px-4 py-2.5 rounded-full bg-navy/5 text-navy text-sm font-semibold hover:bg-navy/10 transition-colors disabled:opacity-50">
          Not now
        </button>
        <button type="button" onClick={confirm} disabled={busy}
          className="flex-1 px-4 py-2.5 rounded-full bg-gold text-white text-sm font-semibold hover:bg-gold-hover active:scale-[0.98] transition-all disabled:opacity-50 disabled:active:scale-100">
          {busy ? 'Confirming…' : reprice ? `Confirm ${money(reprice.total)}` : `Confirm ${money(displayTotal)}`}
        </button>
      </div>
    </div>
  )
}
